import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRefresh } from '@fortawesome/free-solid-svg-icons'
import {
    Combobox,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
} from '@headlessui/react'
import auditApi, {
    AuditLog,
    AuditLogFilters,
    AuditLogAuthStrategy,
    AuditLogEventType,
    AuditLogStatus,
} from '@/api/audit/AuditApi'
import { Pages } from '@/Pages'
import tenantApi from '@/api/tenant/TenantApi'
import { TenantDto } from '@/api/tenant/models/TenantDto'
import { UserDto } from '@/api/tenant/models/UserDto'
import Button from '@/uiV2/common/form/inputs/Button'
import Badge from '@/uiV2/common/badge/Badge'
import { BadgeColor } from '@/uiV2/common/badge/badgeTypes'
import ToggleSwitch3 from '../ToggleSwitch3'

type LoginAuditTabProps = {
    isActive: boolean
}

type FilterFormState = {
    userId: string
    userEmail: string
    tenant: string
    status: AuditLogStatus | ''
    eventType: AuditLogEventType | ''
    authStrategy: AuditLogAuthStrategy | ''
    source: string
    requestId: string
    isNewUser: '' | 'true' | 'false'
    hasUser: '' | 'true' | 'false'
    from: string
    to: string
}

const DEFAULT_FORM_STATE: FilterFormState = {
    userId: '',
    userEmail: '',
    tenant: '',
    status: '',
    eventType: '',
    authStrategy: '',
    source: '',
    requestId: '',
    isNewUser: '',
    hasUser: '',
    from: '',
    to: '',
}

const STATUS_OPTIONS = Object.values(AuditLogStatus)
const EVENT_OPTIONS = Object.values(AuditLogEventType)
const STRATEGY_OPTIONS = Object.values(AuditLogAuthStrategy)
const PAGE_SIZE = 50

const formatTimestamp = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'medium',
    })

const getStatusBadgeColor = (status: AuditLogStatus): BadgeColor =>
    status === AuditLogStatus.SUCCESS ? BadgeColor.Success : BadgeColor.Error

type UserDirectoryEntry = {
    id: string
    name: string
    email: string
}

const normalizeFilters = (
    form: FilterFormState,
    resolveUserId: (value: string) => string | undefined,
): AuditLogFilters => {
    const filters: AuditLogFilters = {}
    if (form.userId.trim()) {
        const resolvedUserId = resolveUserId(form.userId)
        if (resolvedUserId) {
            filters.userId = resolvedUserId
        }
    }
    if (form.userEmail.trim()) filters.userEmail = form.userEmail.trim()
    if (form.tenant.trim()) {
        filters.tenant = form.tenant.trim()
    }
    if (form.status) filters.statuses = [form.status]
    if (form.eventType) filters.eventTypes = [form.eventType]
    if (form.authStrategy) filters.authStrategies = [form.authStrategy]
    if (form.source.trim()) filters.sources = [form.source.trim()]
    if (form.requestId.trim()) filters.requestId = form.requestId.trim()
    if (form.isNewUser) filters.isNewUser = form.isNewUser === 'true'
    if (form.hasUser) filters.hasUser = form.hasUser === 'true'
    if (form.from) filters.from = form.from
    if (form.to) filters.to = form.to
    return filters
}

const getUserDisplayName = (user: UserDto): string => {
    if (user.name?.trim()) {
        return user.name.trim()
    }

    const parts = [user.person?.firstName ?? '', user.person?.lastName ?? '']
    const normalizedName = parts
        .map((part) => part.trim())
        .filter(Boolean)
        .join(' ')

    return normalizedName || user.email
}

export default function LoginAuditTab({
    isActive,
}: LoginAuditTabProps): React.JSX.Element {
    const [formState, setFormState] =
        useState<FilterFormState>(DEFAULT_FORM_STATE)
    const [appliedFilters, setAppliedFilters] =
        useState<FilterFormState>(DEFAULT_FORM_STATE)
    const [pageNumber, setPageNumber] = useState(1)
    const [logs, setLogs] = useState<AuditLog[]>([])
    const [hasNextPage, setHasNextPage] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isExporting, setIsExporting] = useState(false)
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC')
    const [showJobSeekers, setShowJobSeekers] = useState(false)
    const [tenantQuery, setTenantQuery] = useState('')
    const [selectedTenant, setSelectedTenant] = useState<TenantDto | null>(null)
    const [tenantDirectory, setTenantDirectory] = useState<TenantDto[]>([])
    const [userDirectory, setUserDirectory] = useState<UserDirectoryEntry[]>([])
    const hasResults = logs.length > 0

    const filteredTenants = tenantDirectory.filter((t) =>
        t.name.toLowerCase().includes(tenantQuery.toLowerCase()),
    )

    const userLookup = useMemo(() => {
        const map = new Map<string, string>()
        userDirectory.forEach((entry) => {
            map.set(entry.id.toLowerCase(), entry.id)
            if (entry.name) {
                map.set(entry.name.toLowerCase(), entry.id)
            }
            if (entry.email) {
                map.set(entry.email.toLowerCase(), entry.id)
            }
        })
        return map
    }, [userDirectory])

    const resolveUserIdInput = useCallback(
        (value: string): string | undefined => {
            const trimmed = value.trim()
            if (!trimmed) {
                return undefined
            }
            const normalized = trimmed.toLowerCase()
            return userLookup.get(normalized) ?? trimmed
        },
        [userLookup],
    )

    const activeFilters = useMemo(
        () => normalizeFilters(appliedFilters, resolveUserIdInput),
        [appliedFilters, resolveUserIdInput],
    )

    const loadLogs = useCallback(async () => {
        setIsLoading(true)
        setError(null)
        try {
            const res = await auditApi.getAuditLogs({
                pageNumber,
                perPage: PAGE_SIZE,
                sortDirection,
                filters: activeFilters,
            })
            setLogs(res.logs)
            setHasNextPage(res.hasNextPage)
        } catch (err) {
            console.error('Failed to load audit logs', err)
            const message =
                err instanceof Error ? err.message : 'Unable to load audit logs'
            setError(message)
        } finally {
            setIsLoading(false)
        }
    }, [activeFilters, pageNumber, sortDirection])

    const handleApplyFilters = (event: React.FormEvent) => {
        event.preventDefault()
        setAppliedFilters(formState)
        setPageNumber(1)
    }

    const handleResetFilters = () => {
        setFormState(DEFAULT_FORM_STATE)
        setAppliedFilters(DEFAULT_FORM_STATE)
        setSelectedTenant(null)
        setTenantQuery('')
        setPageNumber(1)
    }

    const handleExportCsv = useCallback(async () => {
        if (isExporting) {
            return
        }
        setIsExporting(true)
        setError(null)
        try {
            const allRows: AuditLog[] = []

            const res = await auditApi.getAuditLogs({
                pageNumber: 1,
                sortDirection,
                filters: activeFilters,
            })
            allRows.push(...res.logs)

            const headers = [
                'timestamp',
                'eventType',
                'status',
                'source',
                'authStrategy',
                'userEmail',
                'userId',
                'tenant',
                'requestId',
                'isNewUser',
                'details',
            ]

            const escape = (value: string | null | undefined) => {
                if (value == null) {
                    return ''
                }
                const str = String(value)
                if (
                    str.includes(',') ||
                    str.includes('"') ||
                    str.includes('\n')
                ) {
                    return `"${str.replace(/"/g, '""')}"`
                }
                return str
            }

            const rows = allRows.map((log) =>
                [
                    formatTimestamp(log.timestamp),
                    log.eventType,
                    log.status,
                    log.source ?? '',
                    log.authStrategy ?? '',
                    log.userEmail ?? '',
                    log.user?.id ?? '',
                    log.tenant ?? '',
                    log.requestId ?? '',
                    log.isNewUser ? 'true' : 'false',
                    log.details ?? '',
                ]
                    .map(escape)
                    .join(','),
            )

            const csvContent = [headers.join(','), ...rows].join('\n')
            const blob = new Blob([csvContent], {
                type: 'text/csv;charset=utf-8;',
            })
            const url = URL.createObjectURL(blob)
            const anchor = document.createElement('a')
            anchor.href = url
            anchor.download = `login-audit-${new Date()
                .toISOString()
                .replace(/[:.]/g, '-')}.csv`
            anchor.click()
            URL.revokeObjectURL(url)
        } catch (err) {
            console.error('Failed to export audit logs', err)
            const message =
                err instanceof Error
                    ? err.message
                    : 'Unable to export audit logs'
            setError(message)
        } finally {
            setIsExporting(false)
        }
    }, [activeFilters, isExporting, sortDirection])

    const toggleSortDirection = () => {
        setSortDirection((prev) => (prev === 'DESC' ? 'ASC' : 'DESC'))
        setPageNumber(1)
    }

    useEffect(() => {
        void (async () => {
            try {
                const tenantRes = await tenantApi.getAll({
                    includePersonal: showJobSeekers,
                })
                setTenantDirectory(tenantRes.tenants)
            } catch (err) {
                console.error('Failed to load tenant directory', err)
            }
        })()
    }, [showJobSeekers])

    useEffect(() => {
        if (isActive) {
            void loadLogs()
        }
    }, [isActive, loadLogs])

    useEffect(() => {
        if (!isActive) {
            return
        }

        const term = formState.userId.trim()
        if (!term) {
            setUserDirectory([])
            return
        }

        let isCancelled = false
        const handler = window.setTimeout(() => {
            void (async () => {
                try {
                    const res = await tenantApi.getAllUsers({
                        pageNumber: 1,
                        perPage: 25,
                        searchTerm: term,
                    })
                    if (isCancelled) {
                        return
                    }
                    const directory: UserDirectoryEntry[] = res.users.map(
                        (user) => ({
                            id: user.id,
                            name: getUserDisplayName(user),
                            email: user.email,
                        }),
                    )
                    setUserDirectory(directory)
                } catch (dirError) {
                    if (!isCancelled) {
                        console.error(
                            'Failed to load user suggestions',
                            dirError,
                        )
                    }
                }
            })()
        }, 300)

        return () => {
            isCancelled = true
            window.clearTimeout(handler)
        }
    }, [formState.userId, isActive])

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
                <h3 className="page-title">Login Audit</h3>
                <button
                    type="button"
                    className="rounded-full bg-[#153bab] w-12 h-12 flex items-center justify-center text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={() => {
                        void loadLogs()
                    }}
                    disabled={isLoading}
                    title="Refresh"
                    aria-label="Refresh login audit logs"
                >
                    <FontAwesomeIcon icon={faRefresh} />
                </button>
            </div>

            <button
                type="button"
                className="rounded bg-white px-4 py-2 text-sm font-semibold text-[#153bab] border border-[#153bab] shadow-sm transition hover:bg-[#153bab] hover:text-white disabled:cursor-not-allowed disabled:opacity-60 max-w-fit"
                onClick={handleExportCsv}
                disabled={!hasResults || isLoading || isExporting}
            >
                {isExporting ? 'Exporting\u2026' : 'Export CSV'}
            </button>

            <form
                className="grid gap-4 rounded border border-slate-200 p-4 md:grid-cols-4"
                onSubmit={handleApplyFilters}
            >
                <input
                    type="text"
                    className="form-input"
                    placeholder="User email"
                    value={formState.userEmail}
                    onChange={(event) =>
                        setFormState((prev) => ({
                            ...prev,
                            userEmail: event.target.value,
                        }))
                    }
                />
                <input
                    type="text"
                    className="form-input"
                    placeholder="User ID or name"
                    value={formState.userId}
                    onChange={(event) =>
                        setFormState((prev) => ({
                            ...prev,
                            userId: event.target.value,
                        }))
                    }
                />
                <div>
                    <div className="relative">
                        <Combobox
                            value={selectedTenant}
                            onChange={(tenant: TenantDto | null) => {
                                setSelectedTenant(tenant)
                                setFormState((prev) => ({
                                    ...prev,
                                    tenant: tenant?.id ?? '',
                                }))
                            }}
                        >
                            <ComboboxInput
                                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                placeholder="Search tenant..."
                                displayValue={(t: TenantDto | null) =>
                                    t?.name ?? ''
                                }
                                onChange={(e) => setTenantQuery(e.target.value)}
                            />
                            <ComboboxOptions className="absolute z-10 w-full mt-1 max-h-60 overflow-auto rounded border border-gray-200 bg-white shadow-lg text-sm">
                                {filteredTenants.map((t) => (
                                    <ComboboxOption
                                        key={t.id}
                                        value={t}
                                        className="cursor-pointer px-3 py-2 data-[focus]:bg-gray-100"
                                    >
                                        <span className="flex items-center gap-2">
                                            {t.name}
                                            {t.isPersonal && (
                                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                                                    Job Seeker
                                                </span>
                                            )}
                                        </span>
                                    </ComboboxOption>
                                ))}
                            </ComboboxOptions>
                        </Combobox>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                        <span className="text-sm text-gray-600">
                            Show Job Seekers
                        </span>
                        <ToggleSwitch3
                            value={showJobSeekers}
                            onChange={setShowJobSeekers}
                        />
                    </div>
                </div>
                <input
                    type="text"
                    className="form-input"
                    placeholder="Source surface"
                    value={formState.source}
                    onChange={(event) =>
                        setFormState((prev) => ({
                            ...prev,
                            source: event.target.value,
                        }))
                    }
                />
                <select
                    className="form-input"
                    value={formState.status}
                    onChange={(event) =>
                        setFormState((prev) => ({
                            ...prev,
                            status: event.target.value as AuditLogStatus | '',
                        }))
                    }
                >
                    <option value="">Any status</option>
                    {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                            {status}
                        </option>
                    ))}
                </select>
                <select
                    className="form-input"
                    value={formState.eventType}
                    onChange={(event) =>
                        setFormState((prev) => ({
                            ...prev,
                            eventType: event.target.value as
                                | AuditLogEventType
                                | '',
                        }))
                    }
                >
                    <option value="">Any event</option>
                    {EVENT_OPTIONS.map((eventType) => (
                        <option key={eventType} value={eventType}>
                            {eventType}
                        </option>
                    ))}
                </select>
                <select
                    className="form-input"
                    value={formState.authStrategy}
                    onChange={(event) =>
                        setFormState((prev) => ({
                            ...prev,
                            authStrategy: event.target.value as
                                | AuditLogAuthStrategy
                                | '',
                        }))
                    }
                >
                    <option value="">Any strategy</option>
                    {STRATEGY_OPTIONS.map((strategy) => (
                        <option key={strategy} value={strategy}>
                            {strategy}
                        </option>
                    ))}
                </select>
                <input
                    type="text"
                    className="form-input"
                    placeholder="Request ID"
                    value={formState.requestId}
                    onChange={(event) =>
                        setFormState((prev) => ({
                            ...prev,
                            requestId: event.target.value,
                        }))
                    }
                />
                <select
                    className="form-input"
                    value={formState.isNewUser}
                    onChange={(event) =>
                        setFormState((prev) => ({
                            ...prev,
                            isNewUser: event.target.value as
                                | ''
                                | 'true'
                                | 'false',
                        }))
                    }
                >
                    <option value="">New or existing users</option>
                    <option value="true">New users only</option>
                    <option value="false">Existing users only</option>
                </select>
                <select
                    className="form-input"
                    value={formState.hasUser}
                    onChange={(event) =>
                        setFormState((prev) => ({
                            ...prev,
                            hasUser: event.target.value as
                                | ''
                                | 'true'
                                | 'false',
                        }))
                    }
                >
                    <option value="">Events with or without user</option>
                    <option value="true">Linked user only</option>
                    <option value="false">Orphan events only</option>
                </select>
                <label className="flex flex-col text-xs font-semibold uppercase text-slate-500">
                    From
                    <input
                        type="datetime-local"
                        className="form-input"
                        value={formState.from}
                        onChange={(event) =>
                            setFormState((prev) => ({
                                ...prev,
                                from: event.target.value,
                            }))
                        }
                    />
                </label>
                <label className="flex flex-col text-xs font-semibold uppercase text-slate-500">
                    To
                    <input
                        type="datetime-local"
                        className="form-input"
                        value={formState.to}
                        onChange={(event) =>
                            setFormState((prev) => ({
                                ...prev,
                                to: event.target.value,
                            }))
                        }
                    />
                </label>
                <div className="flex gap-2 md:col-span-4">
                    <Button type="submit" style="primary" enabled={!isLoading}>
                        Apply Filters
                    </Button>
                    <Button
                        type="button"
                        style="secondary"
                        onClick={handleResetFilters}
                        enabled={!isLoading}
                    >
                        Reset
                    </Button>
                </div>
            </form>

            {error && (
                <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="main-table w-full min-w-[1100px] text-sm">
                    <thead>
                        <tr>
                            <th>
                                <button
                                    type="button"
                                    className="flex items-center gap-1 text-left font-semibold"
                                    onClick={toggleSortDirection}
                                    disabled={isLoading}
                                >
                                    <span>Timestamp</span>
                                    <span className="text-xs text-gray-500">
                                        {sortDirection === 'DESC'
                                            ? '\u2193'
                                            : '\u2191'}
                                    </span>
                                </button>
                            </th>
                            <th>Event</th>
                            <th>User</th>
                            <th>Tenant</th>
                            <th>Source</th>
                            <th>Auth Strategy</th>
                            <th>Request ID</th>
                            <th>Details</th>
                            <th>New User?</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading && (
                            <tr>
                                <td
                                    colSpan={9}
                                    className="text-center py-8 text-gray-500"
                                >
                                    Loading audit history{'\u2026'}
                                </td>
                            </tr>
                        )}
                        {!isLoading && logs.length === 0 && (
                            <tr>
                                <td
                                    colSpan={9}
                                    className="text-center py-8 text-gray-500"
                                >
                                    No audit events match your filters.
                                </td>
                            </tr>
                        )}
                        {!isLoading &&
                            logs.map((log) => (
                                <tr
                                    key={log.id}
                                    className={
                                        log.status === AuditLogStatus.ERROR
                                            ? 'bg-red-50'
                                            : ''
                                    }
                                >
                                    <td>
                                        <div className="flex flex-col gap-1">
                                            <span className="font-medium">
                                                {formatTimestamp(log.timestamp)}
                                            </span>
                                            <Badge
                                                color={getStatusBadgeColor(
                                                    log.status,
                                                )}
                                            >
                                                {log.status}
                                            </Badge>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="flex flex-col gap-1">
                                            <span className="font-semibold">
                                                {log.eventType}
                                            </span>
                                            {log.source && (
                                                <span className="text-xs text-gray-500">
                                                    {log.source}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        {log.user ? (
                                            <div className="flex flex-col gap-1">
                                                <Link
                                                    to={`${Pages.UpdateUser}?userId=${log.user.id}`}
                                                    className="link-text"
                                                >
                                                    {log.user.name ||
                                                        log.user.email}
                                                </Link>
                                                <span className="text-xs text-gray-500">
                                                    {log.user.email}
                                                </span>
                                            </div>
                                        ) : log.userEmail ? (
                                            <span>{log.userEmail}</span>
                                        ) : (
                                            <span className="text-gray-400">
                                                None
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {log.tenant ? (
                                            <Link
                                                to={`${Pages.Tenant}?id=${log.tenant}`}
                                                className="link-text"
                                            >
                                                {log.tenant}
                                            </Link>
                                        ) : (
                                            <span className="text-gray-400">
                                                Unknown
                                            </span>
                                        )}
                                    </td>
                                    <td>{log.source ?? '\u2014'}</td>
                                    <td>{log.authStrategy ?? '\u2014'}</td>
                                    <td className="break-all text-xs">
                                        {log.requestId ?? '\u2014'}
                                    </td>
                                    <td className="text-xs text-gray-600">
                                        {log.details ?? '\u2014'}
                                    </td>
                                    <td>
                                        {log.isNewUser === null ? (
                                            '\u2014'
                                        ) : log.isNewUser ? (
                                            <Badge color={BadgeColor.Cyan}>
                                                New user
                                            </Badge>
                                        ) : (
                                            <Badge color={BadgeColor.Neutral}>
                                                Existing
                                            </Badge>
                                        )}
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                <Button
                    type="button"
                    style="secondary"
                    onClick={() =>
                        setPageNumber((prev) => Math.max(1, prev - 1))
                    }
                    enabled={pageNumber > 1 && !isLoading && !isExporting}
                >
                    Previous
                </Button>
                <div>
                    Page {pageNumber} {'\u00B7'} Showing {logs.length}{' '}
                    {'\u00B7'} Per page {PAGE_SIZE}
                </div>
                <Button
                    type="button"
                    style="secondary"
                    onClick={() => setPageNumber((prev) => prev + 1)}
                    enabled={
                        hasNextPage &&
                        !isLoading &&
                        !isExporting &&
                        logs.length > 0
                    }
                >
                    Next
                </Button>
            </div>
        </div>
    )
}
